import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { PRODUCT_BY_HANDLE_QUERY, PRODUCTS_QUERY, storefrontApiRequest, formatMoney, productImageAlt, shopifyImg, shopifySrcSet, shopifySrcSetLarge, type ShopifyProduct } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { Loader2 } from "lucide-react";
import { formatMetafields } from "@/lib/metafields";
import { ga4ItemCategory, trackViewItem } from "@/lib/gtag";
import { ProductBenefits } from "@/components/ProductDetails";
import { ProductSpecSheet } from "@/components/ProductSpecSheet";
import { displayBrand, getProductFacts } from "@/lib/product-facts";
import { SafeHtml } from "@/components/SafeHtml";
import { hreflangLinks } from "@/lib/seo";
import { guardClaims, guardClaimsHtml } from "@/lib/claim-guard";

/**
 * Map a product to a category landing page for breadcrumbs and internal
 * linking. Order matters — the first match wins.
 */
type ProductCategory = {
  slug: string;
  path: string;
  name: string;
  guides?: Array<{ to: string; label: string }>;
};
const CATEGORY_RULES: Array<{ test: RegExp; cat: ProductCategory }> = [
  {
    test: /oppimistorni|helper\s*tower|kitchen\s*tower|learning\s*tower|seisontajakkara|apujakkara/i,
    cat: {
      slug: "oppimistorni",
      path: "/oppimistorni",
      name: "Oppimistornit",
      guides: [
        { to: "/montessori-koulu/oppimistorni-turvallisuus", label: "Oppimistornin turvallisuusopas" },
        { to: "/montessori-koulu/lapsi-keittiossa-ikaopas", label: "Lapsi keittiössä — ikäopas" },
      ],
    },
  },
  {
    test: /pikler|kiipeilykolmio|climbing\s*triangle/i,
    cat: {
      slug: "pikler-kolmio",
      path: "/pikler-kolmio",
      name: "Pikler-kolmiot",
      guides: [
        { to: "/montessori-koulu/pikler-kiipeilyteline-opas", label: "Pikler-kiipeilytelineopas" },
        { to: "/montessori-koulu/kiipeilykolmio-lapselle-ikaopas", label: "Kiipeilykolmio — ikäopas" },
      ],
    },
  },
  {
    test: /kiipeily|climbing|ramppi|ramp|arch/i,
    cat: { slug: "kiipeilyteline", path: "/kiipeilyteline", name: "Kiipeilytelineet" },
  },
  {
    test: /iglu|igloo|majat?|play\s*house|leikkimaja/i,
    cat: { slug: "iglu", path: "/iglu", name: "Iglut & leikkimajat" },
  },
  {
    test: /vaaterekki|vaateteline|clothing\s*rack|clothes\s*rail|kids\s*rack|hanger\s*rack/i,
    cat: {
      slug: "lasten-vaaterekki",
      path: "/lasten-vaaterekki",
      name: "Lasten vaaterekit",
      guides: [{ to: "/montessori-koulu/montessori-kalusteet-kotiin", label: "Montessori-kalusteet kotiin" }],
    },
  },
  {
    test: /s[aä]nky|patja|bed|floor\s*bed|mattress/i,
    cat: {
      slug: "montessori-sanky",
      path: "/montessori-sanky",
      name: "Montessori-sängyt",
      guides: [{ to: "/montessori-koulu/montessori-lattiapatja", label: "Montessori-lattiapatja" }],
    },
  },
  {
    test: /lelu|toy|palapeli|puzzle|helmi|bead/i,
    cat: { slug: "montessori-lelut", path: "/montessori-lelut", name: "Montessori-lelut" },
  },
];
function detectCategory(
  title: string,
  productType?: string,
  tags?: string[],
): ProductCategory | null {
  const hay = [title, productType ?? "", ...(tags ?? [])].join(" ");
  for (const r of CATEGORY_RULES) if (r.test.test(hay)) return r.cat;
  return null;
}

// Below-the-fold — lazy so they don't block LCP or first hydration.
const ProductLightbox = lazy(() => import("@/components/ProductLightbox"));
const RelatedProducts = lazy(() => import("@/components/RelatedProducts"));
const ProductReviewsLazy = lazy(() =>
  import("@/components/ProductDetails").then((m) => ({ default: m.ProductReviews })),
);

export const Route = createFileRoute("/product/$handle")({
  validateSearch: zodValidator(
    z.object({
      // Keep these optional: giving them defaults made the router rewrite
      // every clean product URL to `?market=fi&lang=`, i.e. a crawlable
      // duplicate of the canonical Finnish URL.
      market: fallback(z.enum(["fi", "us"]).optional(), undefined),
      lang: fallback(z.string().optional(), undefined),
    }),
  ),
  loaderDeps: ({ search: { market } }) => ({ market }),
  loader: async ({ params, context, deps }) => {
    const country = deps.market === "us" ? "US" : "FI";
    // Primary locale = curated Finnish copy (see src/lib/shopify.ts).
    const language = "EN";
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["product", params.handle, deps.market],
      queryFn: async () => {
        const res = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, {
          handle: params.handle,
          country,
          language,
        });
        return res?.data?.product ?? null;
      },
    });
    if (!data) throw notFound();
    return { product: data };
  },
  head: ({ params, loaderData }) => {
    const p = loaderData?.product;
    const variant = p?.variants?.edges?.[0]?.node;
    const url = `https://montessori24.fi/product/${params.handle}`;
    const isUs = loaderData?.product && (variant?.price?.currencyCode === "USD");
    const priceNum = variant?.price?.amount ? Math.round(parseFloat(variant.price.amount)) : null;
    const currency = variant?.price?.currencyCode ?? "EUR";
    const priceLabel =
      priceNum != null ? (currency === "USD" ? `$${priceNum}` : `${priceNum} €`) : "";
    const shipCountry = isUs ? "US" : "FI";
    const brand = displayBrand(p?.vendor);
    const tags: string[] = Array.isArray(p?.tags) ? p.tags.filter(Boolean).slice(0, 8) : [];
    // Category detection drives both the title keyword and the
    // description hook, so every product page gets a unique Finnish
    // SEO snippet even when Shopify descriptions are terse or shared.
    const category = p ? detectCategory(p.title ?? "", p.productType, p.tags) : null;
    const headFacts = getProductFacts(params.handle);
    const CATEGORY_KEYWORD: Record<string, string> = {
      "oppimistorni": "oppimistorni",
      "pikler-kolmio": "Pikler-kolmio",
      "kiipeilyteline": "kiipeilyteline",
      "iglu": "leikkimaja",
      "lasten-vaaterekki": "lasten vaaterekki",
      "montessori-sanky": "Montessori-sänky",
      "montessori-lelut": "Montessori-lelu",
    };
    const CATEGORY_HOOK: Record<string, string> = {
      "oppimistorni": "Turvallinen oppimistorni keittiöön.",
      "pikler-kolmio": "Emmi Piklerin filosofiaan pohjautuva kiipeilykolmio.",
      "kiipeilyteline": "Kestävä kiipeilyteline motoriikan kehitykseen.",
      "iglu": "Pehmeä leikkimaja lapsen omaan rauhaan.",
      "lasten-vaaterekki": "Lapsen korkuinen vaaterekki itsenäiseen pukeutumiseen.",
      "montessori-sanky": "Matala Montessori-sänky lapsen itsenäisyyttä tukemaan.",
      "montessori-lelut": "Montessori-lelu luonnollisista materiaaleista.",
    };
    const catKeyword = category ? CATEGORY_KEYWORD[category.slug] : undefined;
    const catHook = category ? CATEGORY_HOOK[category.slug] : undefined;
    // Per-handle SEO differentiator. When two SKUs compete for the
    // same query (e.g. both helper-tower variants ranked pos 44/46 for
    // "seisontajakkara"), append a distinguishing modifier so each
    // product carries its own signal in SERP snippets and titles.
    // /oppimistorni is the primary landing for the bare "seisontajakkara"
    // term; product pages should target long-tail variants instead.
    const HANDLE_MODIFIERS: Record<string, string> = {
      "montessori-helper-tower-step-stool": "seisontajakkara & apujakkara lapselle",
      "montessori-helper-tower-step-stool-kitchen-tower-with-blackboard":
        "seisontajakkara liitutaululla",
    };
    const modifier = params.handle ? HANDLE_MODIFIERS[params.handle] : undefined;
    // Title: category-aware keyword + name + price for uniqueness and
    // CTR. Ordered so the product name is always in the first 60 chars.
    // Suffix pieces are appended only if the title stays under Google's
    // ~60 char snippet limit.
    const buildTitle = () => {
      if (!p) return "Tuote — Montessori24";
      const base = modifier ? `${p.title} — ${modifier}` : p.title;
      const candidates = [
        `${base} – ${priceLabel} | Montessori24`,
        `${base} | Montessori24`,
        base,
      ];
      return candidates.find((c) => c.length <= 60) ?? candidates[candidates.length - 1];
    };
    const title = buildTitle();
    // Description: category hook (unique per family) → product summary →
    // brand → price + shipping signal. Falls back to keyword-rich
    // Finnish copy if Shopify description is empty or too short.
    const plain = guardClaims(p?.description).replace(/\s+/g, " ").trim();
    const shortDesc = plain.length > 20 ? plain : "";
    // The Shopify catalogue is fully Finnish, so a good product
    // description is the best meta description. Only fall back to the
    // category hook when Shopify copy is missing or too short, and skip
    // the brand tag when the description already names the brand.
    const richDesc = plain.length >= 70;
    const hook = richDesc ? "" : (catHook ?? "Montessori-lelu luonnollisista materiaaleista.");
    const brandTag =
      brand && !plain.toLowerCase().includes(brand.toLowerCase()) ? ` ${brand}.` : "";
    const priceTag = priceLabel
      ? ` ${priceLabel}${variant?.availableForSale ? ", varastossa" : ""}.`
      : "";
    const shippingTag = isUs
      ? " Free US shipping."
      : " Ilmainen toimitus Suomeen.";
    const summary = shortDesc
      ? shortDesc
      : `${catKeyword ? `${catKeyword.charAt(0).toUpperCase() + catKeyword.slice(1)} lapselle.` : "Laadukas Montessori-tuote."}`;
    // Finnish copy leads; the (often English) brand name is appended after
    // the description so snippets never open with a foreign brand phrase.
    const descFull = `${hook} ${summary}${brandTag}${priceTag}${shippingTag}`
      .replace(/\s+/g, " ")
      .trim();
    // Cut at a sentence boundary when possible, otherwise a word
    // boundary, so Finnish snippets never break mid-word in SERPs.
    const desc = (() => {
      if (descFull.length <= 158) return descFull;
      const window = descFull.slice(0, 158);
      const lastSentence = Math.max(
        window.lastIndexOf(". "),
        window.lastIndexOf("! "),
        window.lastIndexOf("? "),
      );
      if (lastSentence >= 90) return window.slice(0, lastSentence + 1).trim();
      const lastSpace = window.lastIndexOf(" ");
      return `${window.slice(0, lastSpace > 90 ? lastSpace : 155).trimEnd()}…`;
    })();
    const rawImg = p?.images?.edges?.[0]?.node?.url;
    const img = rawImg ? shopifyImg(rawImg, 1200) : undefined;
    const imgAlt = productImageAlt(p?.images?.edges?.[0]?.node?.altText, p?.title ?? "Montessori24");
    const images = (p?.images?.edges ?? []).map((e: { node: { url: string } }) => e.node.url);
    // ---- Product structured data (schema.org) -------------------------
    // Finnish names/descriptions, per-variant offers with live price and
    // availability, so Google can show price + "Varastossa" in Finnish SERPs.
    type VariantNode = {
      id: string
      title?: string
      sku?: string | null
      price: { amount: string; currencyCode: string }
      compareAtPrice?: { amount: string; currencyCode: string } | null
      availableForSale: boolean
    }
    const variantNodes: VariantNode[] = (p?.variants?.edges ?? []).map(
      (e: { node: VariantNode }) => e.node,
    )
    const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10)
    const shippingDetails = {
      "@type": "OfferShippingDetails",
      shippingRate: { "@type": "MonetaryAmount", value: "0", currency },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: shipCountry },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
        transitTime: {
          "@type": "QuantitativeValue",
          minValue: isUs ? 6 : 3,
          maxValue: isUs ? 10 : 7,
          unitCode: "DAY",
        },
      },
    }
    const returnPolicy = {
      "@type": "MerchantReturnPolicy",
      applicableCountry: shipCountry,
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: 30,
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/FreeReturn",
    }
    const seller = { "@type": "Organization", name: "Montessori24", url: "https://montessori24.fi" }
    const offerFor = (v: VariantNode, index: number) => {
      const list = v.compareAtPrice?.amount ? parseFloat(v.compareAtPrice.amount) : null
      const now = parseFloat(v.price.amount)
      const onSale = list != null && list > now
      return {
        "@type": "Offer",
        "@id": `${url}#offer-${index + 1}`,
        name: variantNodes.length > 1 ? v.title : undefined,
        itemCondition: "https://schema.org/NewCondition",
        sku: v.sku ?? undefined,
        priceCurrency: v.price.currencyCode,
        price: v.price.amount,
        priceValidUntil,
        availability: v.availableForSale
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        url,
        seller,
        shippingDetails,
        hasMerchantReturnPolicy: returnPolicy,
        priceSpecification: onSale
          ? {
              "@type": "UnitPriceSpecification",
              priceType: "https://schema.org/ListPrice",
              price: v.compareAtPrice!.amount,
              priceCurrency: v.compareAtPrice!.currencyCode,
            }
          : undefined,
      }
    }
    const offers = (() => {
      if (!variantNodes.length) return undefined
      if (variantNodes.length === 1) return offerFor(variantNodes[0], 0)
      const amounts = variantNodes.map((v) => parseFloat(v.price.amount))
      return {
        "@type": "AggregateOffer",
        "@id": `${url}#offers`,
        priceCurrency: variantNodes[0].price.currencyCode,
        lowPrice: Math.min(...amounts).toFixed(2),
        highPrice: Math.max(...amounts).toFixed(2),
        offerCount: variantNodes.length,
        availability: variantNodes.some((v) => v.availableForSale)
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        url,
        offers: variantNodes.map(offerFor),
      }
    })()
    const productJsonLd = p && offers ? {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${url}#product`,
      name: p.title,
      description: guardClaims(p.description).trim() || descFull,
      inLanguage: "fi-FI",
      url,
      image: images.length ? images : (img ? [img] : ["https://montessori24.fi/og-image.jpg"]),
      productID: p.id,
      sku: variant?.sku ?? undefined,
      mpn: variant?.sku ?? undefined,
      category: p.productType || "Montessori-lelu",
      brand: { "@type": "Brand", name: displayBrand(p.vendor) ?? "Montessori24" },
      offers,
    } : null;
    const breadcrumbJsonLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Etusivu", item: "https://montessori24.fi/" },
        { "@type": "ListItem", position: 2, name: "Kauppa", item: "https://montessori24.fi/kauppa" },
        ...(() => {
          const cat = category;
          const list: Array<Record<string, unknown>> = [];
          if (cat) {
            const catUrl = `https://montessori24.fi${cat.path}`;
            list.push({
              "@type": "ListItem",
              position: 3,
              name: cat.name,
              item: catUrl,
            });
          }
          if (p) {
            list.push({
              "@type": "ListItem",
              position: cat ? 4 : 3,
              name: p.title,
              item: url,
            });
          }
          return list;
        })(),
      ],
    };
    // FAQ JSON-LD — shipping/return questions eligible for rich result.
    const faqJsonLd = p ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: isUs ? "How long does shipping take?" : "Kuinka nopeasti tuote toimitetaan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: isUs
              ? "Free shipping to the US in 6–10 business days. Orders ship within 1 business day."
              : "Ilmainen toimitus Suomeen 3–7 arkipäivässä. Tilaukset lähtevät 1 arkipäivän kuluessa.",
          },
        },
        {
          "@type": "Question",
          name: isUs ? "What is your return policy?" : "Mikä on palautuskäytäntö?",
          acceptedAnswer: {
            "@type": "Answer",
            text: isUs
              ? "30-day free returns. Items must be unused and in original packaging."
              : "Sinulla on lakisääteinen 14 päivän peruuttamisoikeus. Lisäksi tarjoamme vapaaehtoisen 30 päivän palautusedun: palautus on ilmainen, kun tuote on käyttämätön ja alkuperäisessä pakkauksessa.",
          },
        },
        ...(headFacts.standards?.length
          ? [
              {
                "@type": "Question",
                name: isUs
                  ? "Which safety standards does this product meet?"
                  : "Mitkä turvallisuusstandardit tuote täyttää?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: isUs
                    ? `This product documents: ${headFacts.standards.join(", ")}.`
                    : `Tuotteelle on vahvistettu: ${headFacts.standards.join(", ")}.`,
                },
              },
            ]
          : []),
      ],
    } : null;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        ...(tags.length ? [{ name: "keywords", content: tags.join(", ") }] : []),
        { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "product" },
        { property: "og:site_name", content: "Montessori24" },
        { property: "og:locale", content: "fi_FI" },
        { property: "og:url", content: url },
        ...(img
          ? [
              { property: "og:image", content: img },
              { property: "og:image:secure_url", content: img },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "1200" },
              { property: "og:image:alt", content: imgAlt },
              { name: "twitter:image", content: img },
              { name: "twitter:image:alt", content: imgAlt },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(variant?.price?.amount
          ? [
              { property: "product:price:amount", content: variant.price.amount },
              { property: "product:price:currency", content: currency },
              {
                property: "product:availability",
                content: variant.availableForSale ? "in stock" : "out of stock",
              },
              { property: "product:condition", content: "new" },
            ]
          : []),
        ...(brand ? [{ property: "product:brand", content: brand }] : []),
      ],
      links: [
        { rel: "canonical", href: url },
        ...(rawImg
          ? [
              {
                rel: "preload",
                as: "image",
                href: shopifyImg(rawImg, 800),
                imageSrcSet: shopifySrcSet(rawImg),
                imageSizes: "(min-width: 768px) 50vw, 100vw",
                fetchPriority: "high",
              } as const,
            ]
          : []),
        ...hreflangLinks(url),
      ],
      scripts: [
        ...(productJsonLd ? [{ type: "application/ld+json", children: JSON.stringify(productJsonLd) }] : []),
        { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
        ...(faqJsonLd ? [{ type: "application/ld+json", children: JSON.stringify(faqJsonLd) }] : []),
      ],
    };
  },
  component: ProductPage,
  notFoundComponent: ProductNotFound,
  errorComponent: ProductError,
});

function ProductNotFound() {
  return (
    <Layout>
      <div className="container-prose py-32 text-center">
        <h1 className="text-3xl">Tuotetta ei löytynyt</h1>
        <Link to="/kauppa" className="mt-6 inline-block underline">Takaisin kauppaan</Link>
      </div>
    </Layout>
  );
}

function ProductError({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  return (
    <Layout>
      <div className="container-prose py-32 text-center">
        <h1 className="text-3xl">Tuotteen lataus epäonnistui</h1>
        <p className="mt-4 text-muted-foreground">Yritä uudelleen hetken kuluttua.</p>
        <div className="mt-6 flex justify-center gap-3">
          <button onClick={reset} className="rounded-full border border-border px-6 py-3 text-xs uppercase tracking-[0.2em]">Yritä uudelleen</button>
          <Link to="/kauppa" className="rounded-full bg-foreground px-6 py-3 text-xs uppercase tracking-[0.2em] text-background">Takaisin kauppaan</Link>
        </div>
      </div>
    </Layout>
  );
}

function ProductPage() {
  const { handle } = Route.useParams();
  const { market } = Route.useSearch();
  const { product: initialProduct } = Route.useLoaderData();
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  const [variantIdx, setVariantIdx] = useState(0);
  const [selectedImgIdx, setSelectedImgIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Sticky mobile bar only appears once the real Add-to-cart button has
  // scrolled out of view — no bar while the primary CTA is on screen.
  const atcRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    const el = atcRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const { data } = useQuery({
    queryKey: ["product", handle, market],
    queryFn: async () => {
      const country = market === "us" ? "US" : "FI";
      const language = "EN"; // primary locale holds curated Finnish copy
      const res = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, {
        handle,
        country,
        language,
      });
      return res?.data?.product ?? null;
    },
    initialData: initialProduct,
  });

  if (!data) {
    return (
      <Layout>
        <div className="container-prose py-32 text-center">
          <h1 className="text-3xl">Tuotetta ei löytynyt</h1>
          <Link to="/kauppa" className="mt-6 inline-block underline">Takaisin kauppaan</Link>
        </div>
      </Layout>
    );
  }

  const variants = data.variants.edges.map((e: { node: { id: string; title: string; price: { amount: string; currencyCode: string }; availableForSale: boolean; selectedOptions: Array<{ name: string; value: string }> } }) => e.node);
  const images = data.images.edges.map((e: { node: { url: string; altText: string | null } }) => e.node);
  const videos = (data.media?.edges ?? [])
    .map((e: { node: { mediaContentType: string; sources?: Array<{ url: string; mimeType: string }>; previewImage?: { url: string; altText: string | null } } }) => e.node)
    .filter((n: { mediaContentType: string }) => n.mediaContentType === "VIDEO");
  const heroVideo = videos[0];
  const variant = variants[variantIdx];
  const category = detectCategory(data.title, data.productType, data.tags);
  const facts = getProductFacts(data.handle);

  // Above the fold we show a single sentence, not the whole description.
  const usp = (() => {
    const plain = guardClaims(data.description).replace(/\s+/g, " ").trim();
    if (!plain) return "";
    const first = plain.split(/(?<=[.!?])\s/)[0] ?? plain;
    return first.length > 180 ? `${first.slice(0, 177).trimEnd()}…` : first;
  })();

  const reassurance =
    market === "us"
      ? ["Turvallinen maksu: Visa · Mastercard · Apple Pay"]
      : [
          "Ilmainen toimitus Suomeen, 3–7 arkipäivää",
          "30 päivän palautusetu",
          "Turvallinen maksu: Klarna · MobilePay · kortti",
          ...(facts.standards?.length ? [`Standardit: ${facts.standards.join(" · ")}`] : []),
        ];

  useEffect(() => {
    if (!variant) return;
    trackViewItem({
      id: variant.id,
      name: data.title,
      brand: displayBrand(data.vendor) ?? "Montessori24",
      category: ga4ItemCategory(data.title, data.productType, data.tags),
      variant: variant.title !== "Default Title" ? variant.title : undefined,
      price: parseFloat(variant.price.amount),
      currency: variant.price.currencyCode,
    });
  }, [variant?.id, variant?.title, data.title, data.vendor, data.productType, data.tags, variant?.price.amount, variant?.price.currencyCode]);

  const handleAdd = async () => {
    if (!variant) return;
    await addItem({
      product: { node: data },
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
  };

  return (
    <Layout>
      <section className="container-prose grid gap-12 py-16 md:grid-cols-2 md:py-24">
        <div className="space-y-4">
          {heroVideo && (
            <div className="aspect-[4/5] overflow-hidden rounded-xl bg-black">
              <video
                src={heroVideo.sources?.[0]?.url}
                poster={heroVideo.previewImage?.url}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                controls
                className="h-full w-full object-cover"
              />
            </div>
          )}
          {images[selectedImgIdx] && (
            <button
              onClick={() => setLightboxOpen(true)}
              className="block w-full aspect-[4/5] overflow-hidden rounded-2xl bg-cream cursor-zoom-in"
              aria-label="Avaa kuva suurena"
            >
              <img
                src={shopifyImg(images[selectedImgIdx].url, 960)}
                srcSet={shopifySrcSetLarge(images[selectedImgIdx].url)}
                sizes="(min-width: 768px) 50vw, 100vw"
                width={960}
                height={1200}
                alt={productImageAlt(images[selectedImgIdx].altText, data.title)}
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </button>
          )}
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-3">
              {images.map((img: { url: string; altText: string | null }, i: number) => (
                <button
                  key={i}
                  onClick={() => setSelectedImgIdx(i)}
                  className={`aspect-square overflow-hidden rounded-lg bg-cream transition-all ${
                    i === selectedImgIdx
                      ? "ring-2 ring-foreground ring-offset-2"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Näytä kuva ${i + 1}`}
                >
                  <img
                    src={shopifyImg(img.url, 240)}
                    width={120}
                    height={120}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="md:pl-4">
          <nav aria-label="Murupolku" className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Etusivu</Link>
            <span className="mx-2">/</span>
            <Link to="/kauppa" className="hover:text-foreground">Kauppa</Link>
            {category && (
              <>
                <span className="mx-2">/</span>
                <Link to={category.path} className="hover:text-foreground">
                  {category.name}
                </Link>
              </>
            )}
          </nav>
          <h1 className="mt-4 text-4xl text-foreground md:text-5xl">{data.title}</h1>
          <p className="mt-4 text-2xl text-foreground">{variant && formatMoney(variant.price.amount, variant.price.currencyCode)}</p>
          {variant && (() => {
            const isUs = variant.price.currencyCode === "USD";
            const total = parseFloat(variant.price.amount);
            if (!isFinite(total) || total <= 0) return null;
            if (isUs) {
              // Klarna Pay-in-4 in the US: 4 interest-free installments.
              const per = total / 4;
              const formatted = formatMoney(per, variant.price.currencyCode);
              return (
                <p className="mt-1.5 text-sm text-muted-foreground">
                  or ~<span className="font-medium text-foreground">{formatted}</span> × 4 interest-free payments with Klarna
                </p>
              );
            }
            const monthly = total / 12;
            const formatted = formatMoney(monthly, variant.price.currencyCode);
            return (
              <p className="mt-1.5 text-sm text-muted-foreground">
                tai n. <span className="font-medium text-foreground">{formatted}/kk</span> — jopa 12 kk osamaksu Klarnalla
              </p>
            );
          })()}
          {/* One-sentence USP only — the full description lives in the
              accordions below the purchase area. */}
          {usp && (
            <p className="mt-5 max-w-prose text-[15px] leading-relaxed text-foreground/85">{usp}</p>
          )}

          {variants.length > 1 && (
            <div className="mt-7">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Valitse</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {variants.map((v: { id: string; title: string; availableForSale: boolean }, i: number) => (
                  <button
                    key={v.id}
                    onClick={() => setVariantIdx(i)}
                    disabled={!v.availableForSale}
                    className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                      i === variantIdx ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"
                    } disabled:opacity-40`}
                  >
                    {v.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div ref={atcRef} className="mt-8">
            <button
              onClick={handleAdd}
              disabled={isLoading || !variant?.availableForSale}
              className="inline-flex w-full items-center justify-center rounded-full bg-primary px-8 py-4 text-base font-medium text-primary-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : variant?.availableForSale ? (
                "Lisää ostoskoriin"
              ) : (
                "Loppuunmyyty"
              )}
            </button>
          </div>

          {/* Restrained reassurance — text only, no icon grid, no badges */}
          <ul className="mt-5 space-y-1.5 text-[13px] text-muted-foreground">
            {reassurance.map((line) => (
              <li key={line} className="flex items-start gap-2">
                <span aria-hidden className="mt-px text-foreground/60">✓</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Detailed information — everything below the purchase area, in calm
          accordions. Nothing legally required is hidden: the spec sheet
          renders every verified row it has. */}
      <section className="container-prose pb-4 md:pb-8">
        <div className="mx-auto max-w-3xl">
          {(data.descriptionHtml || data.description) && (
            <details className="group border-b border-border/70" open>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-sm font-medium tracking-wide text-foreground">
                Miksi tämä tuote?
                <span aria-hidden className="text-muted-foreground transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <div className="pb-6">
                {data.descriptionHtml ? (
                  <SafeHtml
                    html={guardClaimsHtml(data.descriptionHtml)}
                    className="space-y-3 leading-relaxed text-muted-foreground [&_strong]:text-foreground [&_a]:text-foreground [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:text-foreground [&_h3]:mt-4 [&_h3]:text-base [&_h3]:text-foreground"
                  />
                ) : (
                  <p className="leading-relaxed text-muted-foreground whitespace-pre-line">
                    {guardClaims(data.description)}
                  </p>
                )}
              </div>
            </details>
          )}

          {(facts.featureList?.length ?? 0) > 0 && (
            <details className="group border-b border-border/70">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-sm font-medium tracking-wide text-foreground">
                Ominaisuudet ja hyödyt
                <span aria-hidden className="text-muted-foreground transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <div className="pb-6">
                <ProductBenefits handle={handle} age={facts.recommendedAge} features={facts.featureList} />
              </div>
            </details>
          )}

          {/* Canonical, verified-only specs: Tuotetiedot / Turvallisuus / Toimitus / Hoito */}
          <ProductSpecSheet
            facts={facts}
            extras={formatMetafields(data.metafields)
              .filter((m) => m.value)
              .map((m) => ({ label: m.label, value: m.value as string }))}
          />

          <p className="mt-6 text-xs text-muted-foreground">
            Maksutavat: Visa · Mastercard · Klarna · MobilePay · Apple Pay
          </p>
        </div>
      </section>

      {/* Reviews — empty placeholder UI until a reviews integration is wired */}
      <section className="container-prose pb-16 md:pb-24">
        <Suspense fallback={null}>
          <ProductReviewsLazy productTitle={data.title} />
        </Suspense>
      </section>

      {/* Related products — one more click, cuts the bounce */}
      <Suspense fallback={null}>
        <RelatedProducts
          productType={data.productType}
          tags={data.tags}
          excludeHandle={data.handle}
        />
      </Suspense>

      {/* Contextual internal links — category landing + guides for topical authority */}
      {category && (
        <section className="border-t border-border">
          <div className="container-prose py-12 md:py-16">
            <h2 className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Lue lisää aiheesta
            </h2>
            <div className="mt-5 flex flex-wrap gap-2">
              <Link
                to={category.path}
                className="rounded-full border border-border px-4 py-2 text-sm text-foreground hover:border-foreground"
              >
                Kaikki: {category.name} →
              </Link>
              {(category.guides ?? []).map((g) => (
                <Link
                  key={g.to}
                  to={g.to}
                  className="rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
                >
                  {g.label} →
                </Link>
              ))}
              <Link
                to="/kauppa"
                className="rounded-full border border-border px-4 py-2 text-sm text-muted-foreground hover:border-foreground hover:text-foreground"
              >
                Selaa koko kauppaa →
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Lightbox — mounted only after the user opens it, keeping Radix Dialog out of the initial bundle */}
      {lightboxOpen && (
        <Suspense fallback={null}>
          <ProductLightbox
            open={lightboxOpen}
            onOpenChange={setLightboxOpen}
            images={images}
            selectedIdx={selectedImgIdx}
            onSelect={setSelectedImgIdx}
            title={data.title}
          />
        </Suspense>
      )}

      {/* Sticky mobile ATC bar — only after the main CTA scrolls away */}
      <div
        className={`fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur transition-all duration-200 md:hidden ${
          showStickyBar ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-full opacity-0"
        }`}
      >
        <div className="container-prose flex items-center gap-3 py-3">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-muted-foreground">{data.title}</p>
            <p className="text-base font-medium text-foreground">
              {variant && formatMoney(variant.price.amount, variant.price.currencyCode)}
            </p>
          </div>
          <button
            onClick={handleAdd}
            disabled={isLoading || !variant?.availableForSale}
            className="inline-flex shrink-0 items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : variant?.availableForSale ? "Lisää koriin" : "Loppu"}
          </button>
        </div>
      </div>
    </Layout>
  );
}
